import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import { requestConsultation } from "../lib/ownerPortal";
import { assetUrl, formatPriceFull, formatInt, formatTypology } from "../lib/format";
import { Lock, Check, ChevronRight } from "lucide-react";
import BackBar from "../components/BackBar";

/**
 * /owner/consultations/new?design=…&parcel=…
 *
 * Three-step flow:
 *   1. Confirm pairing (design + parcel; show fit banner)
 *   2. Project intent + payment ($275 mock)
 *   3. Submission success
 */
export default function RequestConsultation() {
  const [params] = useSearchParams();
  const designId = params.get("design");
  const parcelIdInit = params.get("parcel") || "";
  const { user } = useRole();
  const navigate = useNavigate();

  const [design, setDesign] = useState(null);
  const [parcel, setParcel] = useState(null);
  const [parcelOptions, setParcelOptions] = useState([]);
  const [parcelQuery, setParcelQuery] = useState("");
  const [parcelId, setParcelId] = useState(parcelIdInit);

  const [step, setStep] = useState(1);
  const [note, setNote] = useState("");
  const [organization, setOrganization] = useState(user?.organization || "");
  const [ownerName, setOwnerName] = useState(user?.name || "");

  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [createdConsult, setCreatedConsult] = useState(null);

  useEffect(() => {
    if (!designId) return;
    api.get(`/marketplace/designs/${designId}`).then((r) => setDesign(r.data));
  }, [designId]);

  useEffect(() => {
    if (!parcelId) return;
    api.get(`/parcels/${parcelId}`).then((r) => setParcel(r.data)).catch(() => setParcel(null));
  }, [parcelId]);

  useEffect(() => {
    if (!design || parcel) return;
    api
      .get("/parcels", { params: { only_demo: true } })
      .then((r) => {
        const all = r.data || [];
        const fits = all.filter(
          (p) =>
            (p.width_ft || 0) >= (design.min_lot_width || 0) &&
            (design.zoning_fits || []).includes(p.zoning),
        );
        setParcelOptions(fits.slice(0, 8));
      });
  }, [design, parcel]);

  const fits = useMemo(() => {
    if (!design || !parcel) return null;
    return (
      (parcel.width_ft || 0) >= (design.min_lot_width || 0) &&
      (design.zoning_fits || []).includes(parcel.zoning)
    );
  }, [design, parcel]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await requestConsultation(user.id, {
        parcel_id: parcelId,
        design_id: designId,
        owner_name: ownerName,
        organization,
        note,
        payment: { card_number: cardNumber, expiry, cvc },
      });
      setReceipt(res.receipt);
      setCreatedConsult(res.consultation);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit your request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!designId) {
    return (
      <div data-testid="rc-missing-design">
        <p style={{ color: "#A23B2E" }}>Pick a design from the marketplace first.</p>
        <Link to="/marketplace" className="wn-btn wn-btn-primary mt-4">
          Browse designs
        </Link>
      </div>
    );
  }
  if (!design) {
    return <div style={{ color: "#6C6B85", fontStyle: "italic" }}>Loading…</div>;
  }

  return (
    <section className="animate-fade-in-up" data-testid="request-consultation">
      <BackBar
        parent={{ to: `/marketplace/${designId}`, label: design.name }}
        trail={[
          { label: "Marketplace", to: "/marketplace" },
          { label: design.name, to: `/marketplace/${designId}` },
          { label: "Request consultation" },
        ]}
      />

      <div className="flex items-center gap-3">
        <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Request a consultation
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(28px, 4vw, 36px)",
          fontWeight: 600,
          lineHeight: 1.05,
          color: "#1B1F4B",
        }}
        data-testid="rc-title"
      >
        Begin a conversation with{" "}
        <span style={{ color: "#B14E2F" }}>{design.architect_firm || "this architect"}</span>.
      </h1>
      <p style={{ color: "#6C6B85", fontSize: 14, marginTop: 6 }}>
        A flat <span className="tabular-nums">$275</span> consultation fee covers the parcel
        review, design fit, and first iteration. You'll work directly with the architect from
        there.
      </p>

      <Progress step={step} />

      <div className="mt-8 grid lg:grid-cols-[2fr_1fr] gap-8">
        <div>
          {step === 1 && (
            <Step1
              design={design}
              parcel={parcel}
              parcelOptions={parcelOptions}
              parcelQuery={parcelQuery}
              setParcelQuery={setParcelQuery}
              setParcelId={setParcelId}
              fits={fits}
              onContinue={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step2
              design={design}
              parcel={parcel}
              ownerName={ownerName}
              setOwnerName={setOwnerName}
              organization={organization}
              setOrganization={setOrganization}
              note={note}
              setNote={setNote}
              cardNumber={cardNumber}
              setCardNumber={setCardNumber}
              expiry={expiry}
              setExpiry={setExpiry}
              cvc={cvc}
              setCvc={setCvc}
              error={error}
              submitting={submitting}
              onBack={() => setStep(1)}
              onSubmit={submit}
            />
          )}
          {step === 3 && (
            <Step3
              receipt={receipt}
              consult={createdConsult}
              navigate={navigate}
            />
          )}
        </div>

        <SidebarSummary design={design} parcel={parcel} fits={fits} />
      </div>
    </section>
  );
}

function Progress({ step }) {
  const labels = ["Confirm pairing", "Payment", "Done"];
  return (
    <div className="mt-8 flex items-center gap-3" data-testid="rc-progress">
      {labels.map((l, i) => {
        const n = i + 1;
        const isDone = n < step;
        const isCurrent = n === step;
        return (
          <React.Fragment key={l}>
            <div className="flex items-center gap-2">
              <span
                className="tabular-nums"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: isCurrent ? "#1B1F4B" : isDone ? "#5B6196" : "#FBFAFD",
                  border: "1px solid " + (isCurrent || isDone ? "transparent" : "#D9D8E4"),
                  color: isCurrent || isDone ? "#FBFAFD" : "#6C6B85",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isDone ? <Check size={12} /> : n}
              </span>
              <span style={{ fontSize: 12.5, color: isCurrent ? "#272636" : "#6C6B85" }}>{l}</span>
            </div>
            {n < labels.length && (
              <span aria-hidden style={{ flex: 1, height: 1, background: "#D9D8E4" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Step1({
  design,
  parcel,
  parcelOptions,
  parcelQuery,
  setParcelQuery,
  setParcelId,
  fits,
  onContinue,
}) {
  return (
    <div className="wn-card" style={{ padding: 24 }} data-testid="rc-step-1">
      <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 22 }}>
        Confirm the pairing.
      </h2>
      <p style={{ color: "#6C6B85", fontSize: 13, marginTop: 4 }}>
        Choose the parcel you'd like to pair with this design — we'll pre-flag fit before the
        architect sees the request.
      </p>

      <label className="wn-label mt-6 block">Parcel</label>
      {parcel ? (
        <div className="mt-2 flex items-start justify-between gap-3 wn-card" style={{ padding: 14 }}>
          <div>
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 16,
                color: "#1B1F4B",
              }}
              data-testid="rc-parcel-address"
            >
              {parcel.address}
            </div>
            <div
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 12,
              }}
            >
              {parcel.neighborhood} ·{" "}
              <span className="tabular-nums">
                {parcel.width_ft}′ × {parcel.depth_ft}′
              </span>{" "}
              · Zoning {parcel.zoning}
            </div>
          </div>
          <button
            type="button"
            className="wn-btn wn-btn-tertiary"
            onClick={() => setParcelId("")}
            data-testid="rc-change-parcel"
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            className="wn-input mt-2"
            placeholder="Search address or parcel id"
            value={parcelQuery}
            onChange={(e) => setParcelQuery(e.target.value)}
            data-testid="rc-parcel-search"
          />
          <div className="mt-3 space-y-2">
            {parcelOptions
              .filter(
                (p) =>
                  !parcelQuery ||
                  (p.address || "").toLowerCase().includes(parcelQuery.toLowerCase()) ||
                  (p.parcel_id || "").toLowerCase().includes(parcelQuery.toLowerCase()),
              )
              .map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="wn-card w-full text-left"
                  style={{ padding: 12, cursor: "pointer" }}
                  onClick={() => setParcelId(p.id)}
                  data-testid={`rc-parcel-option-${p.id}`}
                >
                  <div
                    style={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#1B1F4B",
                    }}
                  >
                    {p.address}
                  </div>
                  <div
                    className="tabular-nums"
                    style={{ color: "#6C6B85", fontSize: 12, marginTop: 2 }}
                  >
                    {p.width_ft}′ × {p.depth_ft}′ · {p.zoning} · {p.neighborhood}
                  </div>
                </button>
              ))}
            {parcelOptions.length === 0 && (
              <div style={{ color: "#6C6B85", fontSize: 13 }}>
                We'll show parcels that match this design's lot width and zoning.
              </div>
            )}
          </div>
        </>
      )}

      {parcel && (
        <div
          className="mt-5"
          style={{
            padding: "12px 14px",
            border: "1px solid " + (fits ? "#4F6149" : "#B98A3C"),
            borderRadius: 8,
            background: fits ? "rgba(79,97,73,0.06)" : "rgba(185,138,60,0.08)",
          }}
          data-testid="rc-fit-banner"
        >
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 14,
              color: fits ? "#4F6149" : "#8a651f",
            }}
          >
            {fits
              ? "This pairing looks like a clean fit."
              : "Review recommended — non-standard fit."}
          </div>
          <div style={{ color: "#272636", fontSize: 12.5, marginTop: 4 }}>
            Lot width <span className="tabular-nums">{parcel.width_ft}′</span> · design needs{" "}
            <span className="tabular-nums">{design.min_lot_width}′</span>+ · zoning {parcel.zoning} ·
            design fits {(design.zoning_fits || []).join(", ") || "—"}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          className="wn-btn wn-btn-primary"
          disabled={!parcel}
          onClick={onContinue}
          data-testid="rc-continue-1"
        >
          Continue to payment <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Step2({
  design,
  parcel,
  ownerName,
  setOwnerName,
  organization,
  setOrganization,
  note,
  setNote,
  cardNumber,
  setCardNumber,
  expiry,
  setExpiry,
  cvc,
  setCvc,
  error,
  submitting,
  onBack,
  onSubmit,
}) {
  const ready =
    cardNumber.trim().length >= 12 && expiry.trim().length >= 4 && cvc.trim().length >= 3;

  const parcelAddr = parcel?.address || "__________";
  const parcelNo = parcel?.id || parcel?.parcel_id || "__________";
  const designName = design?.name || "___________";

  const TEMPLATES = [
    {
      key: "parcel_fit",
      label: "Parcel fit consultation",
      blurb: "Confirm the design works on your parcel.",
      body:
        `Hi, I own the parcel at ${parcelAddr}, parcel number ${parcelNo}. `
        + `I really like the ${designName} plan you have in the WHOLE `
        + `Neighborhoods database and would like to ensure that the design `
        + `fits my parcel. Can you please perform a parcel fit consultation `
        + `to ensure compliance.`,
    },
    {
      key: "modifications",
      label: "Quote on modifications",
      blurb: "Pricing for changes + parcel fit, in a 30-minute call.",
      body:
        `Hi, I own the parcel at ${parcelAddr}, parcel number ${parcelNo}. `
        + `I really like the ${designName} plan you have in the WHOLE `
        + `Neighborhoods database but would like to get a quote on making `
        + `the following changes:\n\n  • [e.g. additional bedroom]\n  • `
        + `[e.g. larger front porch]\n  • [e.g. exterior material change]\n\n`
        + `…and would like to ensure that the design fits my parcel. Can we `
        + `please schedule a 30-minute consultation to review options and `
        + `take an initial look at parcel fit?`,
    },
  ];

  function applyTemplate(t) {
    setNote(t.body);
  }

  return (
    <div className="wn-card" style={{ padding: 24 }} data-testid="rc-step-2">
      <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 22 }}>
        About this project, and payment.
      </h2>

      <label className="wn-label mt-6 block">Your name</label>
      <input
        className="wn-input"
        value={ownerName}
        onChange={(e) => setOwnerName(e.target.value)}
        data-testid="rc-owner-name"
      />

      <label className="wn-label mt-4 block">Organization (optional)</label>
      <input
        className="wn-input"
        value={organization}
        onChange={(e) => setOrganization(e.target.value)}
        data-testid="rc-organization"
      />

      <label className="wn-label mt-4 block">Tell the architect about the project</label>
      <div
        className="grid sm:grid-cols-2 gap-2 mt-2"
        data-testid="rc-templates"
      >
        {TEMPLATES.map((t) => {
          const active = note === t.body;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => applyTemplate(t)}
              data-testid={`rc-template-${t.key}`}
              style={{
                textAlign: "left",
                padding: "10px 12px",
                borderRadius: 6,
                border: `1px solid ${active ? "#B14E2F" : "#D9D8E4"}`,
                background: active ? "rgba(177,78,47,0.06)" : "#FBFAFD",
                cursor: "pointer",
                display: "block",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  color: "#1B1F4B",
                  fontSize: 13,
                  letterSpacing: "0.01em",
                }}
              >
                {t.label}
              </div>
              <div
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#6C6B85",
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  marginTop: 2,
                }}
              >
                {t.blurb}
              </div>
            </button>
          );
        })}
      </div>
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          fontSize: 11.5,
          color: "#6C6B85",
          margin: "8px 0 6px",
        }}
      >
        Pick a starter — your parcel address, parcel number, and design name
        are filled in automatically. Edit freely before sending.
      </div>
      <textarea
        className="wn-input"
        style={{ minHeight: 140, resize: "vertical" }}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Family situation, timeline, financing approach, any neighborhood context…"
        data-testid="rc-note"
      />

      <div
        className="mt-7"
        style={{ borderTop: "1px solid #D9D8E4", paddingTop: 16 }}
      >
        <div className="flex items-center gap-2">
          <Lock size={14} style={{ color: "#5B6196" }} />
          <h3
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 17,
              color: "#1B1F4B",
              margin: 0,
            }}
          >
            Consultation fee — $275
          </h3>
        </div>
        <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 4 }}>
          This is a Proof of Concept — payment is simulated. No real card is charged. Use any
          plausible digits to advance.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3 mt-4">
          <div>
            <label className="wn-label block">Card number</label>
            <input
              className="wn-input tabular-nums"
              inputMode="numeric"
              placeholder="4242 4242 4242 4242"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              data-testid="rc-card-number"
            />
          </div>
          <div>
            <label className="wn-label block">Expiry</label>
            <input
              className="wn-input tabular-nums"
              placeholder="MM/YY"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              data-testid="rc-card-expiry"
            />
          </div>
          <div>
            <label className="wn-label block">CVC</label>
            <input
              className="wn-input tabular-nums"
              placeholder="123"
              value={cvc}
              onChange={(e) => setCvc(e.target.value)}
              data-testid="rc-card-cvc"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-4" style={{ color: "#A23B2E", fontSize: 13 }} data-testid="rc-error">
          {error}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button className="wn-btn wn-btn-tertiary" onClick={onBack}>
          Back
        </button>
        <button
          className="wn-btn wn-btn-primary"
          disabled={!ready || submitting}
          onClick={onSubmit}
          data-testid="rc-submit"
        >
          {submitting ? "Submitting…" : "Pay $275 and submit"}
        </button>
      </div>
    </div>
  );
}

function Step3({ receipt, consult, navigate }) {
  return (
    <div className="wn-card" style={{ padding: 28 }} data-testid="rc-step-3">
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          background: "rgba(79,97,73,0.10)",
          color: "#4F6149",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Check size={22} />
      </div>
      <h2
        className="mt-3"
        style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 24 }}
      >
        Request sent to the architect.
      </h2>
      <p style={{ color: "#6C6B85", fontSize: 14, marginTop: 6 }}>
        They have been notified and will reply in the consultation thread. We've recorded a
        receipt for your records.
      </p>

      {receipt && (
        <div
          className="mt-4 wn-card"
          style={{ padding: 14, background: "#FBFAFD" }}
          data-testid="rc-receipt"
        >
          <div className="wn-label">Payment receipt</div>
          <div
            className="tabular-nums"
            style={{ fontSize: 13, color: "#272636", marginTop: 4 }}
          >
            {receipt.transaction_ref} · ${receipt.amount} {receipt.currency} · card ending{" "}
            {receipt.last4}
          </div>
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          className="wn-btn wn-btn-primary"
          onClick={() => navigate(`/owner/consultations/${consult.id}`)}
          data-testid="rc-open-consult"
        >
          Open the consultation
        </button>
        <button className="wn-btn wn-btn-tertiary" onClick={() => navigate("/owner")}>
          Back to dashboard
        </button>
      </div>
    </div>
  );
}

function SidebarSummary({ design, parcel, fits }) {
  return (
    <aside className="wn-card" style={{ padding: 18 }} data-testid="rc-summary">
      {design.exterior_photo && (
        <div
          style={{
            width: "100%",
            height: 140,
            backgroundImage: `url(${assetUrl(design.exterior_photo)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: 4,
          }}
          aria-hidden
        />
      )}
      <div className="mt-3" style={{ fontSize: 11.5, color: "#8F3C23", letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {formatTypology(design.typology)}
      </div>
      <div
        style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 19, color: "#1B1F4B", marginTop: 2 }}
      >
        {design.name}
      </div>
      <div style={{ color: "#6C6B85", fontSize: 12, fontStyle: "italic", fontFamily: '"Newsreader", Georgia, serif' }}>
        {design.architect_firm}
      </div>

      <dl
        className="mt-3 grid grid-cols-2 gap-y-2 gap-x-3 tabular-nums"
        style={{ fontSize: 12.5, color: "#272636" }}
      >
        <dt style={{ color: "#6C6B85" }}>Beds / Baths</dt>
        <dd>{design.beds} / {design.baths}</dd>
        <dt style={{ color: "#6C6B85" }}>Sq Ft</dt>
        <dd>{formatInt(design.sqft)}</dd>
        <dt style={{ color: "#6C6B85" }}>Adjusted 2026</dt>
        <dd>{formatPriceFull(design.adjusted_cost_2026)}</dd>
        <dt style={{ color: "#6C6B85" }}>Plans</dt>
        <dd>{formatPriceFull(design.plan_purchase_price)}</dd>
        <dt style={{ color: "#6C6B85" }}>Soft costs</dt>
        <dd>{formatPriceFull(design.soft_costs)}</dd>
      </dl>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 12, paddingTop: 10 }}>
        <div style={{ color: "#6C6B85", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Consultation fee
        </div>
        <div className="tabular-nums" style={{ color: "#8F3C23", fontWeight: 800, fontSize: 24 }}>
          $275
        </div>
      </div>

      {parcel && (
        <div className="mt-4" style={{ borderTop: "1px solid #D9D8E4", paddingTop: 10 }}>
          <div className="wn-label">Paired parcel</div>
          <div style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 13, color: "#1B1F4B", marginTop: 2 }}>
            {parcel.address}
          </div>
          <div className="tabular-nums" style={{ color: "#6C6B85", fontSize: 12 }}>
            {parcel.width_ft}′ × {parcel.depth_ft}′ · {parcel.zoning}
          </div>
          {fits != null && (
            <div
              className="mt-2"
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: fits ? "#4F6149" : "#8a651f",
              }}
            >
              {fits ? "Fits" : "Review recommended"}
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
